import type { JSX } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { button, card, fieldLabel, formError, input, monoLabel } from '../shell/look';
import { cn } from '@/lib/utils';
import { inviteFormSchema } from '../workspaces/workspace-form';
import type { InviteForm } from '../workspaces/workspace-form';
import { useInvites } from '../workspaces/use-invites';

/**
 * Invite by email: the link goes to that address only, works once, for a
 * week. Accepting it adds them here at the role chosen — signing up first if
 * they have no account. Admin only, like the rest of the Users screen.
 */
export function InvitesPanel(): JSX.Element {
  const form = useForm<InviteForm>({
    resolver: zodResolver(inviteFormSchema),
    defaultValues: { email: '', role: 'dispatcher' },
  });

  const { pending, invite, revoke } = useInvites(() =>
    form.reset({ email: '', role: form.getValues('role') }),
  );

  const problem = form.formState.errors.email?.message ?? invite.error?.message;
  return (
    <div className={cn(card, 'mt-5 flex flex-col gap-4 p-5')}>
      <div className={monoLabel}>INVITE BY EMAIL</div>
      <form
        noValidate
        onSubmit={(event) => void form.handleSubmit((values) => invite.mutate(values))(event)}
        className="flex flex-col gap-3 tab:flex-row tab:items-end"
      >
        <label className={cn(fieldLabel, 'flex-1')}>
          Email
          <input
            {...form.register('email')}
            type="email"
            placeholder="dispatcher@company.com"
            className={input}
          />
        </label>
        <label className={fieldLabel}>
          Role
          <select {...form.register('role')} className={input}>
            <option value="viewer">Viewer</option>
            <option value="dispatcher">Dispatcher</option>
            <option value="admin">Admin</option>
          </select>
        </label>
        <button type="submit" disabled={invite.isPending} className={button.primary}>
          {invite.isPending ? 'Sending…' : 'Send invite'}
        </button>
      </form>
      {problem !== undefined && (
        <div role="alert" className={formError}>
          {problem}
        </div>
      )}
      {(pending.data ?? []).map((item) => (
        <div key={item.id} className="flex items-center justify-between gap-3 text-[13.5px]">
          <span className="truncate">
            {item.email} <span className="text-mute">· {item.role}</span>
          </span>
          <button
            type="button"
            disabled={revoke.isPending}
            onClick={() => revoke.mutate(item.id)}
            className={button.small}
          >
            Revoke
          </button>
        </div>
      ))}
    </div>
  );
}
